import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useUserProfile } from './user-profile';

import { DesignToken } from '~/client/components/base/core';
import { storage } from '~/client/utils/storage';
import * as GQL from '~/graphql/generated/client';
import { OrganizationBranding } from '~/shared/types/anch-report-types';
import { PUBLIC_ROUTES } from '~c/components/public-routes';

function clearOrgCssVars(root: HTMLElement): void {
  root.style.removeProperty('--org-primary-color');
  root.style.removeProperty('--org-primary-contrast');
  root.style.removeProperty('--org-secondary-color');
  root.style.removeProperty('--org-background-color');
}

/** Contrast text color for use on --org-primary-color: dark for light backgrounds, light for dark. */
const CONTRAST_LIGHT = '#ffffff';
const CONTRAST_DARK = '#1f2a2e';

function getContrastForHex(hex: string): string {
  const rgb = parseHexToRgb(hex);
  if (!rgb) {
    return DesignToken.brand.primaryContrast;
  }
  const { r, g, b } = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? CONTRAST_DARK : CONTRAST_LIGHT;
}

/** Hex codes we never use for sidebar (pink/magenta). Normalized to lowercase 6-char. */
const REJECTED_SIDEBAR_HEX = new Set([
  '#ff69b4',
  '#ff1493',
  '#ffc0cb',
  '#ffb6c1',
  '#ff85a2',
  '#ff6b9d',
  '#e91e8c',
  '#f472b6',
  '#ec4899',
  '#db2777',
  '#be185d',
  '#9d174d',
  '#d946ef',
  '#c026d3',
  '#a21caf',
  '#e879f9',
  '#f0abfc',
  '#f9a8d4',
  '#fbcfe8',
  '#fce7f3',
  '#fdf2f8',
]);

function parseHexToRgb(
  hex: string
): { r: number; g: number; b: number } | null {
  const m = hex
    .replace(/^#/, '')
    .match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) {
    return null;
  }
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

/**
 * Returns true if the color is a rejected pink/magenta we never want on the sidebar.
 * Uses blocklist and a simple RGB check (high R + high B, low G = pink/magenta).
 */
function isRejectedSidebarColor(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return true;
  }
  const trimmed = value.trim();
  const hex6 = trimmed.startsWith('#')
    ? trimmed.slice(1, 7).toLowerCase()
    : trimmed.slice(0, 6).toLowerCase();
  if (/^[0-9a-f]{6}$/.test(hex6) && REJECTED_SIDEBAR_HEX.has('#' + hex6)) {
    return true;
  }
  const rgb = parseHexToRgb(trimmed);
  if (rgb) {
    const { r, g, b } = rgb;
    if (r >= 200 && b >= 140 && g <= 180) {
      return true;
    }
    if (r >= 220 && b >= 180 && g >= 100 && g <= 200) {
      return true;
    }
  }
  return false;
}

/** Returns luminance in 0–1. Same formula as getContrastForHex. */
function getLuminance(hex: string): number {
  const rgb = parseHexToRgb(hex);
  if (!rgb) {
    return 0;
  }
  const { r, g, b } = rgb;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

/** Treat white/very light API primary as "no custom branding" so we use default teal on sidebar. */
function isTooLightForSidebar(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return false;
  }
  return getLuminance(value.trim()) >= 0.95;
}

/** Use default teal for sidebar when org color is rejected (pink/magenta) or too light (e.g. white). */
const SIDEBAR_FALLBACK = DesignToken.brand.primary;

// List of public route prefixes where we should skip user queries
const PUBLIC_ROUTE_PREFIXES = Object.values(PUBLIC_ROUTES).map((route) =>
  route.split(':')[0].replace(/\/$/, '')
);

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

const BRANDING_OVERRIDE_KEY = 'use-org-branding';

function readBrandingOverride(): boolean {
  const val = storage.getJSON<boolean>(BRANDING_OVERRIDE_KEY);
  return val !== false;
}

// Query to get organization branding by specific organization ID

interface OrganizationBrandingContextType {
  branding: OrganizationBranding | null;
  loading: boolean;
  organizationName?: string;
  refetch: () => void;
  /** Set the current org (e.g. after switching or after editing branding). Updates sidebar branding. */
  setCurrentOrgId: (orgId: string | null) => void;
  useOrgBranding: boolean;
  setUseOrgBranding: (value: boolean) => void;
}

const OrganizationBrandingContext =
  createContext<OrganizationBrandingContextType>({
    branding: null,
    loading: false,
    refetch: () => {},
    setCurrentOrgId: () => {},
    useOrgBranding: true,
    setUseOrgBranding: () => {},
  });

interface Props {
  children: React.ReactNode;
}

export const OrganizationBrandingProvider: React.FC<Props> = ({ children }) => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Only fetch user profile on authenticated pages
  const { userProfile } = useUserProfile({ skip: isPublic });
  const [currentOrgId, setCurrentOrgIdState] = useState<string | null>(null);
  const [useOrgBranding, setUseOrgBrandingState] = useState<boolean>(() =>
    readBrandingOverride()
  );

  const setUseOrgBranding = React.useCallback((value: boolean) => {
    setUseOrgBrandingState(value);
    if (value) {
      storage.remove(BRANDING_OVERRIDE_KEY);
    } else {
      storage.setJSON(BRANDING_OVERRIDE_KEY, false);
    }
  }, []);

  // Get current organization ID from localStorage or userProfile
  useEffect(() => {
    if (isPublic) {
      return;
    }
    const storedOrgId = storage.get('currentOrgId');

    // If no stored orgId but we have a defaultOrganizationId, persist it
    if (!storedOrgId && userProfile.defaultOrganizationId) {
      storage.set('currentOrgId', userProfile.defaultOrganizationId);
      setCurrentOrgIdState(userProfile.defaultOrganizationId);
    } else {
      const orgId = storedOrgId ?? userProfile.defaultOrganizationId;
      setCurrentOrgIdState(orgId ?? null);
    }
  }, [userProfile.defaultOrganizationId, isPublic]);

  const { data, loading, refetch } = GQL.useGetOrganizationBrandingQuery({
    variables: { organizationId: currentOrgId ?? '' },
    skip: !currentOrgId || isPublic,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const organization = data?.organizationById;
  const branding = organization?.branding ?? null;
  const loadedOrgId = organization?._id ?? null;

  const hasBranding =
    branding &&
    (branding.primaryColor ||
      branding.secondaryColor ||
      branding.backgroundColor ||
      branding.logoUrl);

  // When switching org, clear CSS vars immediately so we never show the previous org's colors (e.g. pink from cache).
  useEffect(() => {
    if (!currentOrgId || isPublic) {
      return;
    }
    clearOrgCssVars(document.documentElement);
  }, [currentOrgId, isPublic]);

  // Apply branding only when the loaded data is for the current org (avoids stale/cached branding from another org).
  useEffect(() => {
    const root = document.documentElement;

    if (loading || loadedOrgId !== currentOrgId) {
      clearOrgCssVars(root);
      return;
    }

    if (hasBranding && branding && useOrgBranding) {
      const primary =
        branding.primaryColor && !isTooLightForSidebar(branding.primaryColor)
          ? branding.primaryColor
          : SIDEBAR_FALLBACK;
      const background =
        branding.backgroundColor &&
        !isTooLightForSidebar(branding.backgroundColor)
          ? branding.backgroundColor
          : SIDEBAR_FALLBACK;
      const secondary = branding.secondaryColor
        ? branding.secondaryColor
        : null;

      root.style.setProperty('--org-primary-color', primary);
      root.style.setProperty(
        '--org-primary-contrast',
        getContrastForHex(primary)
      );
      root.style.setProperty('--org-background-color', background);
      if (secondary) {
        root.style.setProperty('--org-secondary-color', secondary);
      } else {
        root.style.removeProperty('--org-secondary-color');
      }
    } else {
      clearOrgCssVars(root);
    }
  }, [
    loading,
    loadedOrgId,
    currentOrgId,
    hasBranding,
    branding?.primaryColor,
    branding?.secondaryColor,
    branding?.backgroundColor,
    useOrgBranding,
  ]);

  const setCurrentOrgId = React.useCallback((orgId: string | null) => {
    if (orgId) {
      storage.set('currentOrgId', orgId);
    } else {
      storage.remove('currentOrgId');
    }
    setCurrentOrgIdState(orgId);
  }, []);

  const value = {
    branding: branding
      ? {
          logoUrl: branding.logoUrl ?? undefined,
          primaryColor: branding.primaryColor ?? undefined,
          secondaryColor: branding.secondaryColor ?? undefined,
          backgroundColor: branding.backgroundColor ?? undefined,
          accentPalette: branding.accentPalette ?? undefined,
        }
      : null,
    loading,
    organizationName: organization?.name,
    refetch,
    setCurrentOrgId,
    useOrgBranding,
    setUseOrgBranding,
  };

  return (
    <OrganizationBrandingContext.Provider value={value}>
      {children}
    </OrganizationBrandingContext.Provider>
  );
};

export const useOrganizationBranding = () => {
  const context = useContext(OrganizationBrandingContext);
  if (!context) {
    throw new Error(
      'useOrganizationBranding must be used within OrganizationBrandingProvider'
    );
  }
  return context;
};

export default useOrganizationBranding;
